import { getId } from '@fluentui/react';
import type { DebouncedFunc, DebounceSettings } from 'lodash';
import debounce from 'lodash.debounce';
import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useAuth } from 'react-oidc-context';
import { useHistory } from 'react-router-dom';

import {
  App,
  AppAttribute,
  AppInstance,
  ListAppInstancesRequest,
  ListAppsRequest,
  UpdateAppPreferenceRequest,
} from '@src/gen/ai/h2o/cloud/appstore/v1/ai.h2o.cloud.appstore';
import type { Workspace } from '@src/gen/ai/h2o/workspace/v1/workspace_pb';
import { CardBadge, useTheme } from '@shared-legacy/ui-kit';
import {
  AdminCenterTimeFilterContext,
  AdminCenterTimeFilterContextType,
  BannerContext,
  CloudPlatformDiscoveryContext,
  EnvContext,
  LeftPanelContext,
  UserContext,
} from '@shared/contexts';
import {
  clearLoggingOut,
  logLogoutRaceEvent,
  logSignoutRedirect,
  setLoggingOut,
  shouldRevokeTokensOnSignout,
} from '@shared/lib/auth/logoutGuard';
import { useToast } from '@shared/lib/ui/components-alpha';
import { handleErrMsg, InstancePauseResumeOp, openSafeExternalUrl, redirect } from '@shared/lib/utils';
import { LocalStorageKeys, SessionStorageKeys } from '@shared/lib/utils/localStorage';
import { AnyFunction, Banner } from '@shared/models';
import { AdminAppService, AppService } from '@shared/services/appstore/api';
import { TwirpErrorResponse } from '@shared/services/appstore/HTTPResponse';
import { defaultAppstoreWorkspaceName } from '@shared/services/authz/authz.hooks';
import { useWorkspaces } from '@shared/services/authz/providers/WorkspaceProvider';

// Re-export LocalStorageKeys for convenience
export { LocalStorageKeys };

const APP_DETAILS_BASE_PATH = '/apps/';
const INSTANCE_LOG_BASE_PATH = '/instances/log/';
const ADMIN_INSTANCE_LOG_BASE_PATH = '/admin/instances/log/';

export function useDialog(): [boolean, () => void] {
  const [isDialogHidden, setIsDialogHidden] = useState(true),
    toggleDialog = useCallback(() => setIsDialogHidden(!isDialogHidden), [isDialogHidden, setIsDialogHidden]);
  return [isDialogHidden, toggleDialog];
}

export function useError(): [
  TwirpErrorResponse | null,
  Dispatch<SetStateAction<TwirpErrorResponse | null>>,
  () => void
] {
  const [err, setErr] = useState<TwirpErrorResponse | null>(null),
    dismissErr = useCallback(() => setErr(null), [setErr]);
  return [err, setErr, dismissErr];
}

export type QueryParamProps = {
  name: string;
  value: string;
};

export function useQueryParams() {
  const history = useHistory(),
    params = useMemo(() => {
      const parsedParams = new URLSearchParams(window.location.search);
      for (const key of parsedParams.keys()) {
        if (!isSafeQueryParamName(key)) parsedParams.delete(key);
      }
      return parsedParams;
    }, [window.location.search]),
    clearParams = useCallback(() => {
      history.replace({ search: '' });
    }, [history]),
    clearParam = useCallback(
      (name: string) => {
        if (!isSafeQueryParamName(name)) return;
        const currentSearch = new URLSearchParams(window.location.search);
        currentSearch.delete(name);
        history.replace({ search: currentSearch.toString() });
      },
      [params, history]
    ),
    setParams = useCallback(
      (input: QueryParamProps | QueryParamProps[]) => {
        const currentSearch = new URLSearchParams(window.location.search);
        const source = Array.isArray(input) ? input : [input];
        source.forEach(({ name, value }) => {
          if (!isSafeQueryParamName(name)) return;
          if (value) {
            currentSearch.set(name, value);
          } else {
            currentSearch.delete(name);
          }
        });
        history.replace({ search: currentSearch.toString() });
      },
      [params, history]
    );

  return { params, clearParams, clearParam, setParams };
}

export type Refinement<T> = (key: string, data: T[]) => T[];
export type FilterRefinement<T> = (filters: Record<string, string>, data: T[]) => T[];

interface UseRefinementsArgs<T> {
  data?: T[] | null;
  onSearch?: Refinement<T>;
  defaultSearchKey?: string;
  onSort?: Refinement<T>;
  defaultSortKey?: string;
  onFilter?: FilterRefinement<T>;
  defaultFilterKey?: Record<string, string>;
}

const RefinementNoOp = <T>(_: string, data: T[]): T[] => data;
const FilterRefinementNoOp = <T>(_: Record<string, string>, data: T[]): T[] => data;

export enum ParamKeys {
  Filter = 'category',
  Hint = 'hint',
  Search = 'q',
  Sort = 'sort',
}

const UNSAFE_QUERY_PARAM_NAMES = new Set(['__proto__', 'prototype', 'constructor']);

const isSafeQueryParamName = (name: string) => !UNSAFE_QUERY_PARAM_NAMES.has(name);

function isFilterKey(key: string) {
  return key !== ParamKeys.Search && key !== ParamKeys.Sort && key !== ParamKeys.Hint;
}

function getInitialKey(urlParams: URLSearchParams, paramKey: ParamKeys, defaultKey?: string) {
  const param = urlParams.get(paramKey);
  return param || defaultKey || '';
}

function getInitialFilterKey(urlParams: URLSearchParams, defaultKey?: Record<string, string>): Record<string, string> {
  const filterEntries: Array<[string, string]> = [];
  for (const [key, value] of urlParams) {
    if (isFilterKey(key) && isSafeQueryParamName(key)) {
      filterEntries.push([key, value || defaultKey?.[key] || '']);
    }
  }
  return Object.fromEntries(filterEntries);
}

export function useRefineData<T>(args: UseRefinementsArgs<T>) {
  const { data, onSearch, defaultSearchKey, onSort, defaultSortKey, onFilter, defaultFilterKey } = args;
  const { params, clearParams, clearParam, setParams } = useQueryParams();
  const [searchKey, setSearchKey] = useState<string>(getInitialKey(params, ParamKeys.Search, defaultSearchKey));
  const [sortKey, setSortKey] = useState<string>(getInitialKey(params, ParamKeys.Sort, defaultSortKey));
  const [filterMap, setFilterMap] = useState<Record<string, string>>(getInitialFilterKey(params, defaultFilterKey));
  const refinedData = useMemo(() => {
    const searchedData = (onSearch || RefinementNoOp)(searchKey, data || []);
    const filteredData = (onFilter || FilterRefinementNoOp)(filterMap, searchedData);
    return (onSort || RefinementNoOp)(sortKey, filteredData);
  }, [data, searchKey, sortKey, filterMap, onSearch, onFilter, onSort]);
  const setFilter = useCallback((key: string, value: string) => {
    if (!isSafeQueryParamName(key)) return;
    setFilterMap((old) => ({ ...old, [key]: value }));
  }, []);

  useEffect(() => {
    setParams([
      {
        name: ParamKeys.Search,
        value: searchKey,
      },
      {
        name: ParamKeys.Sort,
        value: sortKey,
      },
      ...Object.entries(filterMap).map(([key, value]) => ({ name: key, value })),
    ]);
  }, [searchKey, sortKey, filterMap, setParams]);

  const resetSearch = useCallback(() => {
    setSearchKey(defaultSearchKey || '');
    clearParam(ParamKeys.Search);
  }, [clearParam, defaultSearchKey]);
  const resetSort = useCallback(() => {
    setSortKey(defaultSortKey || '');
    clearParam(ParamKeys.Sort);
  }, [clearParam, defaultSortKey]);
  const resetFilter = useCallback(
    (key: string) => {
      setFilter(key, defaultFilterKey?.[key] || '');
      clearParam(key);
    },
    [clearParam, defaultFilterKey, setFilter]
  );
  const resetAllFilters = useCallback(() => {
    Object.keys(filterMap || {}).forEach((key) => {
      setFilter(key, defaultFilterKey?.[key] || '');
      clearParam(key);
    });
  }, [filterMap, setFilter, defaultFilterKey, clearParam]);
  const resetAll = useCallback(() => {
    setSearchKey(defaultSearchKey || '');
    setSortKey(defaultSortKey || '');
    Object.keys(filterMap || {}).forEach((key) => setFilter(key, defaultFilterKey?.[key] || ''));
    clearParams();
  }, [clearParams, filterMap, setFilter, defaultSearchKey, defaultSortKey, defaultFilterKey]);

  return {
    data: refinedData || [],
    searchKey,
    sortKey,
    filterMap,
    setSearchKey,
    setSortKey,
    setFilter,
    resetSearch,
    resetSort,
    resetFilter,
    resetAllFilters,
    resetAll,
  };
}

export function useCategory() {
  const getCategory = useCallback((newFilterMap: Record<string, string>) => newFilterMap?.[ParamKeys.Filter] || '', []),
    setCategory = useCallback((setFilter: (key: string, value: string) => void, value: string) => {
      setFilter(ParamKeys.Filter, value);
    }, []),
    resetCategory = useCallback((resetFilter: (key: string) => void) => resetFilter(ParamKeys.Filter), []);

  return { getCategory, setCategory, resetCategory };
}

export function useBanner() {
  const { banners, dispatch } = useContext(BannerContext);
  const add = useCallback(
    (bannerOptionalId: Omit<Banner, 'id'> & { id?: string }) => {
      const banner: Banner = { id: getId(`@@banner@@`), ...bannerOptionalId };
      dispatch({ type: `Add`, payload: banner });
      return banner;
    },
    [dispatch]
  );
  const remove = useCallback((bannerId: string) => dispatch({ type: `Remove`, payload: { id: bannerId } }), [dispatch]);
  return { banners, add, remove } as const;
}

export const useUser = () => useContext(UserContext);

export const useLeftPanel = () => useContext(LeftPanelContext);

export const useEnv = () => useContext(EnvContext);

export const useCloudPlatformDiscovery = () => useContext(CloudPlatformDiscoveryContext);

export function useApp() {
  const theme = useTheme();
  const { addToast } = useToast();
  const getApps = useCallback(async (params: ListAppsRequest, pinned = false) => {
    const { apps = [] } = await AppService.listApps(params);
    // default order: ORDER BY created_at DESC
    return pinned ? apps.filter((app) => app.preference?.pinned) : apps;
  }, []);
  const getAdminApps = useCallback(async (params: ListAppsRequest) => {
    const { apps = [] } = await AdminAppService.listApps(params);
    return apps;
  }, []);
  const getAppBadges = useCallback(
    (app: App): CardBadge[] => {
      const badges = app.attributes.map((appAttribute) => {
        switch (appAttribute) {
          case AppAttribute.DEPRECATED:
            return {
              label: 'Deprecated',
              color: theme.palette?.gray800 || '',
            };
          case AppAttribute.MVP:
            return {
              label: 'MVP',
              color: theme.palette?.yellow500 || '',
            };
          case AppAttribute.PREVIEW:
            return {
              label: 'Preview',
              color: theme.palette?.yellow500 || '',
            };
          case AppAttribute.SUPPORTED:
            return {
              label: 'H2O.ai Supported',
              color: theme.palette?.yellow500 || '',
            };
          case AppAttribute.ATTRIBUTE_UNSPECIFIED:
          default:
            return undefined;
        }
      });
      return badges.filter(Boolean) as CardBadge[];
    },
    [theme]
  );
  const updateAppPreference = useCallback(
    async (params: UpdateAppPreferenceRequest, errorMessage: string) => {
      let app: App | null = null;
      try {
        const res = await AppService.updateAppPreference(params);
        app = res.app;
      } catch (error: unknown) {
        if (error instanceof Error) {
          addToast({
            type: 'error',
            message: `${errorMessage}: ${handleErrMsg(error.message)}`,
          });
        }
      }
      return app;
    },
    [addToast]
  );
  const updatePin = useCallback(
    async (app: App) => {
      const { id, preference } = app;
      const { liked, pinned } = preference;

      return await updateAppPreference(
        { id, liked: { value: liked }, pinned: { value: !pinned } },
        `An error has occurred while ${pinned ? 'un' : ''}pinning this app`
      );
    },
    [updateAppPreference]
  );
  const updateLikes = useCallback(
    async (app: App) => {
      const { id, preference } = app;
      const { pinned, liked } = preference;

      return await updateAppPreference(
        { id, pinned: { value: pinned }, liked: { value: !liked } },
        `An error has occurred while ${liked ? 'un' : ''}liking this app`
      );
    },
    [updateAppPreference]
  );
  return { getApps, getAdminApps, getAppBadges, updatePin, updateLikes };
}

type ListAppInstancesRequestPayload = Omit<ListAppInstancesRequest, 'parent'> & {
  parent?: string;
};

export function useInstance() {
  const { ACTIVE_WORKSPACE_NAME = defaultAppstoreWorkspaceName } = useWorkspaces();
  const history = useHistory();
  const { addToast } = useToast();

  const getInstancesList = useCallback(
    async (params: ListAppInstancesRequestPayload) => {
      const { instances } = await AppService.listAppInstances({
        ...params,
        parent: params.parent || ACTIVE_WORKSPACE_NAME,
      });
      return instances;
    },
    [ACTIVE_WORKSPACE_NAME]
  );
  const getAdminInstancesList = useCallback(
    async (params: ListAppInstancesRequestPayload) => {
      const { instances } = await AdminAppService.listAppInstances({
        ...params,
        parent: params.parent || ACTIVE_WORKSPACE_NAME,
      });
      return instances;
    },
    [ACTIVE_WORKSPACE_NAME]
  );
  const setInstanceSuspension = useCallback(
    async ({ id }: AppInstance, op: InstancePauseResumeOp, isAdmin = false, parent = ACTIVE_WORKSPACE_NAME) => {
      const { suspend, description, openInstance } = op;
      try {
        const service = isAdmin ? AdminAppService : AppService;
        const { instance } = await service.setAppInstanceSuspension({ id, suspend, parent });
        if (openInstance) openSafeExternalUrl(instance.location);
        return instance;
      } catch (error: any) {
        addToast({
          type: 'error',
          message: `Could not ${description} app instance: ${handleErrMsg(error.message)}`,
        });
        throw error;
      }
    },
    [addToast, ACTIVE_WORKSPACE_NAME]
  );
  const setInstancesSuspension = useCallback(
    async (instances: AppInstance[], op: InstancePauseResumeOp, isAdmin = false, parent = ACTIVE_WORKSPACE_NAME) => {
      const { suspend, description } = op;
      const service = isAdmin ? AdminAppService : AppService;
      const results = await Promise.all(
        instances
          .map(({ id }: AppInstance) => service.setAppInstanceSuspension({ id, suspend, parent }))
          .map((p) => p.catch((e) => e))
      );
      const messages: string[] = [];
      results.forEach((result, i) => {
        if (result instanceof Error) {
          const app = instances[i].appDetails;
          const instanceId = instances[i].id;
          messages.push(`Instance ID: ${instanceId} (App Name: ${app.name} / App version:${app.version})`);
        }
      });
      if (messages.length > 0) {
        addToast({
          type: 'error',
          message: `Could not ${description} app instance: ${messages.join(', ')}}`,
        });
      }
    },
    [addToast, ACTIVE_WORKSPACE_NAME]
  );
  const terminateInstance = useCallback(
    async (instance: AppInstance, isAdmin = false, parent = ACTIVE_WORKSPACE_NAME) => {
      const { id } = instance;
      try {
        const service = isAdmin ? AdminAppService : AppService;
        await service.terminateAppInstance({ id, parent });
      } catch (message: any) {
        addToast({
          type: 'error',
          message: `Could not terminate app instance: ${handleErrMsg(message.message)}`,
        });
      }
    },
    [addToast, ACTIVE_WORKSPACE_NAME]
  );
  const goToInstance = useCallback(
    (instance: AppInstance) => () => {
      redirect(instance.location);
    },
    []
  );
  const goToInstanceLog = useCallback(
    (instance: AppInstance, isAdmin = false) =>
      () => {
        const path = isAdmin
          ? `${ADMIN_INSTANCE_LOG_BASE_PATH}${instance.id}`
          : `${INSTANCE_LOG_BASE_PATH}${instance.id}`;
        history.push(path, { parent: instance.parent });
      },
    [history]
  );
  const goToAppDetail = useCallback(
    (appId: string) => () => {
      // TODO: when Admin App Details are developed, link to Admin App Detail view
      history.push(`${APP_DETAILS_BASE_PATH}${appId}`, { from: 'instances' });
    },
    [history]
  );
  const getInstance = useCallback(
    async (instanceId: string, parent = ACTIVE_WORKSPACE_NAME, includeAppDetails = false) => {
      const appInstance = await AppService.getAppInstance({ id: instanceId, includeAppDetails, parent });
      return appInstance.instance;
    },
    [ACTIVE_WORKSPACE_NAME]
  );
  const getAdminInstance = useCallback(
    async (instanceId: string, parent = ACTIVE_WORKSPACE_NAME, includeAppDetails = false) => {
      const appInstance = await AdminAppService.getAppInstance({
        id: instanceId,
        includeAppDetails,
        parent,
      });
      return appInstance.instance;
    },
    [ACTIVE_WORKSPACE_NAME]
  );

  return {
    getInstance,
    getAdminInstance,
    getInstancesList,
    getAdminInstancesList,
    setInstanceSuspension,
    setInstancesSuspension,
    terminateInstance,
    goToInstance,
    goToAppDetail,
    goToInstanceLog,
  };
}

export function useInterval(callback: AnyFunction, delay: number | null) {
  const savedCallbackRef = useRef<AnyFunction>();

  savedCallbackRef.current = callback;

  useEffect((): AnyFunction | void => {
    const handler = (...args: any[]) => savedCallbackRef.current!(...args);

    if (delay !== null) {
      const intervalId = setInterval(handler, delay);
      return () => clearInterval(intervalId);
    }
  }, [delay]);
}

/**
 * Combines React.useCallback with lodash.debounce
 * @param {Function} callback to be memoized and debounced
 * @param {number | null} wait the number of milliseconds to delay invocation.
 * Pass `null` to use RequestAnimationFrame. This config cannot be changed
 * during mount.
 * @param {DebounceSettings} [debounceSettings] lodash debounce settings. This
 * config cannot be changed during mount.
 * @return {Function}
 */
export function useDebouncedCallback<Callback extends (...args: any[]) => any>(
  callback: Callback,
  wait: number | null,
  debounceSettings?: DebounceSettings
): DebouncedFunc<Callback> {
  const callbackRef = useRef<Callback>(callback);

  callbackRef.current = callback;

  const [debouncedCallback] = useState(() => {
    return debounce((...args) => callbackRef.current(...args), wait as any, debounceSettings);
  });
  useEffect(
    () => () => {
      debouncedCallback.cancel();
    },
    []
  );
  return debouncedCallback;
}

/**
 * Debounced derivation of a value: returns `value` but only after it has stopped changing for `wait`
 * milliseconds. Lets callers derive debounced state from an existing source (props, form/store state)
 * without wiring their own effect + setState.
 * @param {T} value the source value to debounce
 * @param {number | null} wait milliseconds to wait; `null` uses RequestAnimationFrame
 * @param {DebounceSettings} [debounceSettings] lodash debounce settings
 * @return {T} the debounced value
 */
export function useDebouncedValue<T>(value: T, wait: number | null, debounceSettings?: DebounceSettings): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const setDebounced = useDebouncedCallback(setDebouncedValue, wait, debounceSettings);
  useEffect(() => {
    setDebounced(value);
  }, [value, setDebounced]);
  return debouncedValue;
}

export type UsePromiseCallbackSettlement<Callback extends (...args: any[]) => Promise<any>> = {
  error?: any;
  data?: Awaited<ReturnType<Callback>>;
  mountedRef: Readonly<RefObject<boolean>>;
};

/**
 * Utility to integrate promise with React, it manages the `loading` state, and
 * it prevents propagating the promise result when the component unmounts.
 * @param {Function} callback - a function that returns Promise,
 * @param {any[]} dependencies - dependencies of the callback (same as
 * `React.useCallback` or `React.useMemo`).
 * @param {object} [options]
 * @property {Function} options.onError - callback, that will be invoked when
 * the promise rejects. Called only when this hook is mounted.
 * @property {Function} options.onSuccess - callback, that will be invoked when
 * the promise resolves. Called only when this hook is mounted.
 * @property {Function} options.onSettled - callback, that will be invoked as
 * the promise settles. Called always, even when the hook is unmounted. The
 * callback receives an object with either `result` in case of resolved promise
 * or `error` in case of rejected promise. The object also contains a ref object
 * which holds the "mounted" state of the hook - it is `true` when the component
 * is mounted and changes to `false` when the component unmounts.
 * @return {Array<*> & { 0: Function, 1: boolean }} usePromiseCallbackResult
 * @property {Function} usePromiseCallbackResult.0 - passed callback, memoized
 * the same way `React.useCallback` works. The Promise returned by this callback
 * never rejects to prevent unhandled rejection error. The result of the promise
 * is communicated via UsePromiseCallbackSettlement object. The object contains
 * also a `mountedRef` property, that should be checked before propagating the
 * Promise settlement.
 * @property {boolean} usePromiseCallbackResult.1 - the 'loading' state of the
 * returned callback, if `true` the promise is pending.
 */
export function usePromiseCallback<Callback extends (...args: any[]) => Promise<any>>(
  callback: Callback,
  dependencies: readonly any[],
  options: {
    onError?: (error: any) => any;
    onSuccess?: (result: Awaited<ReturnType<Callback>>) => any;
    onSettled?: (settlement: UsePromiseCallbackSettlement<Callback>) => any;
  } = {}
): [(...args: Parameters<Callback>) => Promise<UsePromiseCallbackSettlement<Callback>>, boolean] {
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const [loading, setLoading] = useState<boolean>(false);
  const memoizedCallback = useCallback((...args: Parameters<Callback>) => {
    setLoading(true);
    return callback(...args)
      .finally(() => {
        if (mountedRef.current) {
          setLoading(false);
        }
      })
      .then(
        (data) => {
          if (mountedRef.current) {
            optionsRef.current.onSuccess?.(data);
          }
          const settlement = { mountedRef, data };
          optionsRef.current?.onSettled?.(settlement);
          return settlement;
        },
        (error) => {
          if (mountedRef.current) {
            optionsRef.current.onError?.(error);
          }
          const settlement = { mountedRef, error };
          optionsRef.current?.onSettled?.(settlement);
          return settlement;
        }
      );
    // eslint-disable-next-line react-hooks/use-memo
  }, dependencies);
  return [memoizedCallback as any, loading];
}

/**
 * This function dispatches a StorageEvent for the current window. Other tags
 * are notified automatically, but the current window is not notified, that's
 * why we need this function.
 */
const dispatchStorageEvent = (key: string, newValue: string | null) => {
  window.dispatchEvent(new StorageEvent('storage', { key, newValue }));
};

const setLocalStorageItem = (key: string, value: any) => {
  const stringifiedValue = JSON.stringify(value);
  window.localStorage.setItem(key, stringifiedValue);
  dispatchStorageEvent(key, stringifiedValue);
};

const removeLocalStorageItem = (key: string) => {
  window.localStorage.removeItem(key);
  dispatchStorageEvent(key, null);
};

const getLocalStorageItem = (key: string) => {
  return window.localStorage.getItem(key);
};

const subscribeLocalStorage = (callback: (e: StorageEvent) => void) => {
  window.addEventListener('storage', callback);
  return () => window.removeEventListener('storage', callback);
};

export function useLocalStorage<T>(key: LocalStorageKeys, initialValue?: T) {
  const [storedStringValue, setStoredStringValue] = useState<string | null>(() => getLocalStorageItem(key));

  useEffect(() => {
    return subscribeLocalStorage((e: StorageEvent) => {
      if (e.key === key) setStoredStringValue(e.newValue);
    });
  }, [key]);

  const storedParsedValue = useMemo(() => {
    if (storedStringValue === null) {
      return initialValue;
    }
    try {
      return JSON.parse(storedStringValue) as T;
    } catch (error) {
      console.error(`Error parsing localStorage key "${key}":`, error);
      return initialValue;
    }
  }, [storedStringValue, key]);
  const parsedValueRef = useRef<T | null | undefined>(storedParsedValue);
  useEffect(() => {
    parsedValueRef.current = storedParsedValue;
  }, [storedParsedValue]);

  const setValue = useCallback(
    (valueOrFn: T | null | undefined | ((v: T | null | undefined) => T)) => {
      let value: T | null | undefined;
      if (typeof valueOrFn === 'function') {
        value = (valueOrFn as (v: T | null | undefined) => T)(parsedValueRef.current as T | null | undefined);
      } else {
        value = valueOrFn as T | null | undefined;
      }
      if (value == null) {
        removeLocalStorageItem(key);
      } else {
        setLocalStorageItem(key, value);
      }
    },
    [key]
  );

  return [storedParsedValue, setValue] as const;
}

/**
 * In-memory pub/sub scoped to the current tab (module scope = one instance per
 * document). It lets multiple `useSessionStorage` hooks using the SAME key in
 * the SAME tab observe each other's writes.
 *
 * We intentionally do NOT use the `storage` event here: it fires across tabs
 * sharing the origin (and never fires in the window that performed the write),
 * which is exactly the cross-tab coupling we want to avoid. `sessionStorage` is
 * already per-tab; this event target only re-introduces the same-tab reactivity
 * that React needs to re-render sibling consumers.
 */
const sessionStorageEventTarget = new EventTarget();
const sessionStorageEventName = (key: string) => `session-storage:${key}`;

const setSessionStorageItem = (key: string, value: any) => {
  window.sessionStorage.setItem(key, JSON.stringify(value));
  sessionStorageEventTarget.dispatchEvent(new Event(sessionStorageEventName(key)));
};

const removeSessionStorageItem = (key: string) => {
  window.sessionStorage.removeItem(key);
  sessionStorageEventTarget.dispatchEvent(new Event(sessionStorageEventName(key)));
};

const getSessionStorageItem = (key: string) => window.sessionStorage.getItem(key);

const subscribeSessionStorage = (key: string, callback: () => void) => {
  const eventName = sessionStorageEventName(key);
  sessionStorageEventTarget.addEventListener(eventName, callback);
  return () => sessionStorageEventTarget.removeEventListener(eventName, callback);
};

/**
 * Like `useLocalStorage`, but backed by `sessionStorage`, so the value is
 * per-browser-tab: it survives a reload and a brief round-trip to an external
 * site (e.g. payment gateway, OIDC), but is NOT shared across tabs. Multiple
 * instances on the same key within one tab stay in sync via an in-memory event
 * target (see above).
 */
export function useSessionStorage<T>(key: SessionStorageKeys, initialValue?: T) {
  const storedStringValue = useSyncExternalStore(
    useCallback((callback: () => void) => subscribeSessionStorage(key, callback), [key]),
    () => getSessionStorageItem(key)
  );

  const storedParsedValue = useMemo(() => {
    if (storedStringValue === null) {
      return initialValue;
    }
    try {
      return JSON.parse(storedStringValue) as T;
    } catch (error) {
      console.error(`Error parsing sessionStorage key "${key}":`, error);
      return initialValue;
    }
  }, [storedStringValue, key]);

  const parsedValueRef = useRef<T | null | undefined>(storedParsedValue);
  useEffect(() => {
    parsedValueRef.current = storedParsedValue;
  }, [storedParsedValue]);

  const setValue = useCallback(
    (valueOrFn: T | null | undefined | ((v: T | null | undefined) => T)) => {
      let value: T | null | undefined;
      if (typeof valueOrFn === 'function') {
        value = (valueOrFn as (v: T | null | undefined) => T)(parsedValueRef.current as T | null | undefined);
      } else {
        value = valueOrFn;
      }
      if (value == null) {
        removeSessionStorageItem(key);
      } else {
        setSessionStorageItem(key, value);
      }
    },
    [key]
  );

  return [storedParsedValue, setValue] as const;
}

/**
 * Returns the value `value` had on the previous render (undefined on first
 * render). Useful to detect a transition, e.g. distinguishing a value that just
 * changed from one that merely differs from some other input.
 */
export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

const getMimeTypeFromUrl = (url: string): string => {
  const extension = url.split('.').pop()?.split('?')[0]?.toLowerCase();

  switch (extension) {
    case 'png':
      return 'image/png';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'svg':
      return 'image/svg+xml';
    case 'ico':
      return 'image/x-icon';
    default:
      return 'image/x-icon';
  }
};

export const useDynamicFavicon = (faviconUrl: string) => {
  useEffect(() => {
    if (!faviconUrl) return;

    const mimeType = getMimeTypeFromUrl(faviconUrl);

    const existingLink = document.querySelector("link[rel*='icon']") as HTMLLinkElement | null;
    const link = existingLink || (document.createElement('link') as HTMLLinkElement);

    link.rel = 'icon';
    link.type = mimeType;
    link.href = faviconUrl;

    if (!existingLink) {
      document.head.appendChild(link);
    }
  }, [faviconUrl]);
};

export const useMemoCustomCompare = <Result, Dependencies>(
  cb: () => Result,
  compare: (prev: Dependencies, next: Dependencies) => boolean,
  dependencies: Dependencies
) => {
  const prevDependenciesRef = useRef<null | { dependencies: Dependencies }>(null);
  const hasChanged =
    prevDependenciesRef.current === null || compare(prevDependenciesRef.current.dependencies, dependencies);
  prevDependenciesRef.current = { dependencies };
  const recomputeTriggerRef = useRef(true);
  if (hasChanged) {
    recomputeTriggerRef.current = !recomputeTriggerRef.current;
  }
  // eslint-disable-next-line react-hooks/use-memo
  return useMemo(cb, [recomputeTriggerRef.current]);
};

export const useHandleSignOut = () => {
  const auth = useAuth();
  const [, setActiveWorkspace] = useSessionStorage<Workspace | undefined>(SessionStorageKeys.ACTIVE_WORKSPACE);
  return async () => {
    setActiveWorkspace(undefined);
    setLoggingOut();
    logSignoutRedirect();
    // Opt-in only. Revoking tokens here (as revokeTokensOnSignout=true used to do) can terminate the
    // session-bound Keycloak session before end_session, which skips federated IdP (e.g. Okta) logout.
    // Kept behind config so environments that rely on the old behavior can re-enable it per-deployment.
    // Read from module state, not EnvContext: this hook is also called from the error pages, which
    // ErrorBoundary renders outside every provider — see setRevokeTokensOnSignout.
    if (shouldRevokeTokensOnSignout()) {
      try {
        await auth.revokeTokens();
      } catch (error) {
        logLogoutRaceEvent('revokeTokens failed before signout', { error: String(error) });
      }
    }
    try {
      return await auth.signoutRedirect();
    } finally {
      // A redirect that takes effect tears this document down, so control only reaches here when
      // logout did not navigate away — react-oidc-context swallows navigator failures and resolves
      // null — or when the document came back (e.g. restored from the back/forward cache). Either
      // way the suppression must be lifted, or automatic sign-in stays blocked until a manual reload.
      clearLoggingOut('signoutRedirect returned without navigating away');
    }
  };
};

export const useAdminCenterTimeFilter = (): AdminCenterTimeFilterContextType => {
  const context = useContext(AdminCenterTimeFilterContext);
  if (context === undefined) {
    throw new Error('useAdminCenterTimeFilter must be used within a AdminCenterTimeFilterProvider');
  }
  return context;
};

export {
  getBackDelta,
  getPreviousPathname,
  subscribePreviousLocation,
  useTrackPreviousLocation,
} from './previousLocationTracking';
export {
  createSearchParams,
  getSearchParamsForLocation,
  type ParamKeyValuePair,
  type SetURLSearchParams,
  type URLSearchParamsInit,
  useSearchParams,
} from './searchParams';
export { useGoBack, type UseGoBackOptions } from './useGoBack';
export { useIsDarkMode } from './useIsDarkMode';
export { useSyncedRef } from './useSyncedRef';
